<script lang="ts">
  import { X } from 'lucide-svelte'
  
  interface Props {
    title?: string
    message: string
    confirmText?: string
    cancelText?: string
    showCancel?: boolean
    confirmButtonClass?: string
    onConfirm: () => void
    onCancel?: () => void
  }
  
  let { 
    title = '확인',
    message,
    confirmText = '확인',
    cancelText = '취소',
    showCancel = true,
    confirmButtonClass = '',
    onConfirm,
    onCancel
  }: Props = $props()
  
  function handleCancel() {
    if (onCancel) {
      onCancel()
    }
  }
  
  function handleConfirm() {
    onConfirm()
  }
  
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && showCancel) {
      handleCancel()
    } else if (e.key === 'Enter') {
      handleConfirm()
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div 
  class="modal-overlay" 
  role="dialog"
  aria-modal="true"
  tabindex="-1"
  onclick={showCancel ? handleCancel : undefined}
  onkeydown={(e) => {
    if (e.key === 'Escape' && showCancel) {
      handleCancel()
    }
  }}>
  <div 
    class="modal-content" 
    role="presentation"
    onclick={(e) => e.stopPropagation()}
    onkeydown={(e) => e.stopPropagation()}>
    <div class="modal-header">
      <h3>{title}</h3>
      {#if showCancel}
        <button class="close-button" onclick={handleCancel}>
          <X size={16} />
        </button>
      {/if}
    </div>
    
    <div class="modal-body">
      <p>{message}</p>
    </div>
    
    <div class="modal-footer">
      {#if showCancel}
        <button class="button cancel" onclick={handleCancel}>
          {cancelText}
        </button>
      {/if}
      <button class="button confirm {confirmButtonClass}" onclick={handleConfirm}>
        {confirmText}
      </button>
    </div>
  </div>
</div>

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    animation: fadeIn 0.15s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .modal-content {
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    min-width: 360px;
    max-width: 480px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    animation: slideIn 0.15s ease-out;
  }
  
  @keyframes slideIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .modal-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
  }
  
  .close-button:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-body p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.5;
  }
  
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--border-primary);
  }
  
  .button {
    padding: 6px 14px;
    border-radius: 6px;
    border: 1px solid var(--border-primary);
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  
  .button:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }
  
  .button.confirm {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }
  
  .button.confirm:hover {
    background: var(--accent-primary);
    opacity: 0.9;
    filter: brightness(0.92);
    border-color: var(--accent-primary);
  }
  
  .button.danger {
    background: #dc2626;
    border-color: #dc2626;
    color: white;
  }
  
  .button.danger:hover {
    background: #dc2626;
    opacity: 0.9;
    filter: brightness(0.92);
    border-color: #dc2626;
  }
</style>